// Smart item parser for extracting quantity, unit, and item name
// Provides Things3/Bear-like natural language parsing

const QUANTITY_PATTERNS = [
  // Numbers with fractions
  /^(\d+(?:\.\d+)?(?:\/\d+)?)\s*(.+)/,
  // Fractions only
  /^(\d+\/\d+)\s*(.+)/,
  // Words for numbers
  /^(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*(.+)/i,
  // A/an patterns
  /^(a|an)\s*(.+)/i
];

const UNIT_PATTERNS = [
  // Weight units
  { pattern: /\b(pounds?|lbs?|ounces?|oz|grams?|g|kilograms?|kg)\b/i, type: 'weight' },
  // Volume units
  { pattern: /\b(gallons?|gal|quarts?|qt|pints?|pt|cups?|c|fluid\s*ounces?|fl\s*oz|liters?|l|milliliters?|ml)\b/i, type: 'volume' },
  // Count units
  { pattern: /\b(dozen|doz|pack|packs|box|boxes|bag|bags|bottle|bottles|can|cans|jar|jars|container|containers|piece|pieces|pc|pcs|each|ea)\b/i, type: 'count' },
  // Length units
  { pattern: /\b(feet|ft|inches?|in|yards?|yd|meters?|m|centimeters?|cm)\b/i, type: 'length' }
];

const NUMBER_WORDS = {
  'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
  'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
  'eleven': 11, 'twelve': 12, 'a': 1, 'an': 1
};

export function parseShoppingItem(input) {
  if (!input || typeof input !== 'string') {
    return { quantity: null, unit: null, name: input || '' };
  }

  const trimmed = input.trim();
  let quantity = null;
  let unit = null;
  let name = trimmed;

  // Try to extract quantity first
  for (const pattern of QUANTITY_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) {
      const quantityPart = match[1].toLowerCase();
      const remainder = match[2].trim();

      // Convert word numbers to digits
      if (NUMBER_WORDS[quantityPart]) {
        quantity = NUMBER_WORDS[quantityPart];
      } else if (!isNaN(quantityPart)) {
        quantity = parseFloat(quantityPart);
      } else if (quantityPart.includes('/')) {
        // Handle fractions
        const [num, den] = quantityPart.split('/');
        quantity = parseFloat(num) / parseFloat(den);
      }

      name = remainder;
      break;
    }
  }

  // Try to extract unit from the remaining text
  if (name) {
    for (const unitPattern of UNIT_PATTERNS) {
      const match = name.match(unitPattern.pattern);
      if (match) {
        unit = match[1];
        // Remove the unit from the name
        name = name.replace(unitPattern.pattern, '').trim();
        // Clean up extra spaces
        name = name.replace(/\s+/g, ' ');
        break;
      }
    }
  }

  // If we found a quantity but no unit, try to infer common units based on item
  if (quantity && !unit) {
    const inferredUnit = inferUnit(name);
    if (inferredUnit) {
      unit = inferredUnit;
    }
  }

  // Normalize unit names
  if (unit) {
    unit = normalizeUnit(unit);
  }

  return {
    quantity,
    unit,
    name: name.trim()
  };
}

function inferUnit(itemName) {
  const name = itemName.toLowerCase();
  
  // Liquid items typically use gallons/quarts
  if (/(milk|juice|water|soda|beer|wine|oil|vinegar|sauce)/.test(name)) {
    return 'gallon';
  }
  
  // Produce typically uses pounds
  if (/(apple|banana|orange|potato|onion|carrot|tomato|lettuce|spinach)/.test(name)) {
    return 'lb';
  }
  
  // Meat typically uses pounds
  if (/(chicken|beef|pork|fish|turkey|ham|bacon)/.test(name)) {
    return 'lb';
  }
  
  // Packaged goods typically use count
  if (/(bread|cereal|pasta|rice|chips|crackers|cookies)/.test(name)) {
    return 'box';
  }
  
  return null;
}

function normalizeUnit(unit) {
  const normalized = unit.toLowerCase();
  
  // Weight units
  if (['pound', 'pounds', 'lb', 'lbs'].includes(normalized)) return 'lb';
  if (['ounce', 'ounces', 'oz'].includes(normalized)) return 'oz';
  if (['gram', 'grams', 'g'].includes(normalized)) return 'g';
  if (['kilogram', 'kilograms', 'kg'].includes(normalized)) return 'kg';
  
  // Volume units
  if (['gallon', 'gallons', 'gal'].includes(normalized)) return 'gallon';
  if (['quart', 'quarts', 'qt'].includes(normalized)) return 'quart';
  if (['pint', 'pints', 'pt'].includes(normalized)) return 'pint';
  if (['cup', 'cups', 'c'].includes(normalized)) return 'cup';
  if (['fluid ounce', 'fluid ounces', 'fl oz', 'fl. oz'].includes(normalized)) return 'fl oz';
  if (['liter', 'liters', 'l'].includes(normalized)) return 'liter';
  if (['milliliter', 'milliliters', 'ml'].includes(normalized)) return 'ml';
  
  // Count units
  if (['dozen', 'doz'].includes(normalized)) return 'dozen';
  if (['pack', 'packs'].includes(normalized)) return 'pack';
  if (['box', 'boxes'].includes(normalized)) return 'box';
  if (['bag', 'bags'].includes(normalized)) return 'bag';
  if (['bottle', 'bottles'].includes(normalized)) return 'bottle';
  if (['can', 'cans'].includes(normalized)) return 'can';
  if (['jar', 'jars'].includes(normalized)) return 'jar';
  if (['container', 'containers'].includes(normalized)) return 'container';
  if (['piece', 'pieces', 'pc', 'pcs'].includes(normalized)) return 'piece';
  if (['each', 'ea'].includes(normalized)) return 'each';
  
  return unit; // Return original if no normalization found
}

export function formatShoppingItem(item) {
  const { quantity, unit, name } = item;
  
  if (!quantity && !unit) {
    return name;
  }
  
  let result = '';
  
  if (quantity) {
    result += quantity === 1 ? '1' : quantity.toString();
    if (unit) {
      result += ' ' + unit;
    }
  } else if (unit) {
    result += unit;
  }
  
  if (result && name) {
    result += ' ' + name;
  } else if (name) {
    result = name;
  }
  
  return result;
}

// Examples of what this parser can handle:
// "2 gallon milk" -> { quantity: 2, unit: "gallon", name: "milk" }
// "gallon of milk" -> { quantity: null, unit: "gallon", name: "of milk" }
// "dozen eggs" -> { quantity: null, unit: "dozen", name: "eggs" }
// "3 dozen eggs" -> { quantity: 3, unit: "dozen", name: "eggs" }
// "a loaf of bread" -> { quantity: 1, unit: null, name: "loaf of bread" }
// "two pounds chicken" -> { quantity: 2, unit: "lb", name: "chicken" }
// "milk" -> { quantity: null, unit: null, name: "milk" }
// "1/2 gallon milk" -> { quantity: 0.5, unit: "gallon", name: "milk" }